import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { behavioursApi, clientsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, LogOut, Brain, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Smile, Plus, Trash2, Loader2, Activity
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { LogoHome } from '../components/LogoHome';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

const SEVERITY_COLORS = {
  high: 'text-red-500 bg-red-500/10 border-red-500/30',
  medium: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
  low: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
  positive: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30',
};

const TREND_ICONS = { improving: TrendingDown, worsening: TrendingUp, stable: Minus };

export default function BehaviourTrackingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [categories, setCategories] = useState({ default: [], custom: [], all: [] });
  const [loading, setLoading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [newBehaviourName, setNewBehaviourName] = useState('');
  const [newBehaviourSeverity, setNewBehaviourSeverity] = useState('medium');

  useEffect(() => {
    loadClients();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedClient) loadSummary();
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      const res = await clientsApi.getAll();
      setClients(res.data);
    } catch (err) { /* silent */ }
  };

  const loadCategories = async () => {
    try {
      const res = await behavioursApi.getCategories();
      setCategories(res.data);
    } catch (err) { /* silent */ }
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const [sumRes, compRes] = await Promise.all([
        behavioursApi.getClientSummary(selectedClient),
        behavioursApi.getClientComparison(selectedClient),
      ]);
      setSummary(sumRes.data);
      setComparison(compRes.data);
    } catch (err) {
      setSummary(null);
      setComparison(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyseAll = async () => {
    if (!selectedClient) return;
    setAnalysing(true);
    try {
      const res = await behavioursApi.analyseAll(selectedClient);
      toast.success(`Analysed ${res.data.analysed} reports`);
      loadSummary();
    } catch (err) {
      toast.error('Analysis failed');
    } finally {
      setAnalysing(false);
    }
  };

  const handleAiScore = async () => {
    if (!selectedClient) return;
    setAnalysing(true);
    try {
      const res = await behavioursApi.generateAiScores(selectedClient);
      toast.success(`AI scores generated for ${res.data.client_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scoring failed');
    } finally {
      setAnalysing(false);
    }
  };

  const handleAddCustom = async () => {
    if (!newBehaviourName.trim()) return;
    try {
      await behavioursApi.addCustomBehaviour({ name: newBehaviourName.trim(), severity: newBehaviourSeverity });
      toast.success('Custom behaviour added');
      setNewBehaviourName('');
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDeleteCustom = async (id) => {
    try {
      await behavioursApi.deleteCustomBehaviour(id);
      toast.success('Removed');
      loadCategories();
    } catch (err) { toast.error('Failed'); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';

  const navItems = [
    { to: backRoute, label: 'Dashboard', icon: <ArrowLeft className="h-5 w-5" /> },
    { to: '/behaviour-tracking', label: 'Behaviour Tracking', icon: <Brain className="h-5 w-5" />, active: true },
  ];

  // Chart data
  const chartData = summary?.categories?.filter(c => c.severity_level !== 'positive').map(c => ({
    name: c.name.length > 15 ? c.name.slice(0, 15) + '...' : c.name,
    count: c.count,
    severity: c.avg_severity,
  })) || [];

  const positiveData = summary?.categories?.filter(c => c.severity_level === 'positive') || [];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="behaviour-tracking-page">
      <MobileNav navItems={navItems} user={user} onLogout={handleLogout} />

      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to={backRoute} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-600/30">
              <Brain className="h-5 w-5" /> Behaviour Tracking
            </div>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">{user?.name?.charAt(0)}</div>
            <div><p className="text-white font-medium text-sm">{user?.name}</p><p className="text-slate-500 text-xs capitalize">{user?.role}</p></div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="lg:pl-64">
        <div className="p-6 lg:p-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">AI Behaviour Tracking</h1>
          <p className="text-slate-400 mb-6">AI-powered analysis of client behaviours from shift reports</p>

          {/* Client selector + actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white sm:w-64" data-testid="behaviour-client-select">
                <SelectValue placeholder="Select Client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleAnalyseAll} disabled={!selectedClient || analysing} className="bg-teal-600 hover:bg-teal-700" data-testid="analyse-all-btn">
              {analysing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
              Analyse Reports
            </Button>
            <Button onClick={handleAiScore} disabled={!selectedClient || analysing} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="ai-score-btn">
              <TrendingUp className="h-4 w-4 mr-2" /> Generate AI Scores
            </Button>
          </div>

          {/* Behaviour Summary */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div></div>
          ) : summary ? (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-5">
                  <p className="text-xs text-slate-400">Total Records</p>
                  <p className="text-2xl font-bold text-white">{summary.total_records}</p>
                </CardContent></Card>
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-5">
                  <p className="text-xs text-slate-400">Behaviour Types</p>
                  <p className="text-2xl font-bold text-white">{summary.categories?.length || 0}</p>
                </CardContent></Card>
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-5">
                  <p className="text-xs text-slate-400">High Severity</p>
                  <p className="text-2xl font-bold text-red-400">{summary.categories?.filter(c => c.severity_level === 'high').length || 0}</p>
                </CardContent></Card>
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-5">
                  <p className="text-xs text-slate-400">Positive Behaviours</p>
                  <p className="text-2xl font-bold text-emerald-400">{positiveData.length}</p>
                </CardContent></Card>
              </div>

              {/* Chart */}
              {chartData.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Behaviour Frequency & Severity</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="count" fill="#EF4444" name="Occurrences" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="severity" fill="#F59E0B" name="Avg Severity" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Category breakdown */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white text-base">Behaviour Categories</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {summary.categories?.map(cat => {
                      const TrendIcon = TREND_ICONS[cat.trend] || Minus;
                      return (
                        <div key={cat.category} className={`p-4 rounded-lg border ${SEVERITY_COLORS[cat.severity_level] || SEVERITY_COLORS.medium}`} data-testid={`behaviour-cat-${cat.category}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {cat.severity_level === 'positive' ? <Smile className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                              <span className="font-medium text-sm">{cat.name}</span>
                              <Badge variant="outline" className="text-[10px]">{cat.count}x</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <TrendIcon className="h-4 w-4" />
                              <span className="text-xs capitalize">{cat.trend?.replace('_', ' ')}</span>
                              <Badge variant="outline" className="text-xs">Avg: {cat.avg_severity}/10</Badge>
                            </div>
                          </div>
                          {cat.common_triggers?.length > 0 && (
                            <p className="text-xs opacity-75 mt-1">Triggers: {cat.common_triggers.join(', ')}</p>
                          )}
                        </div>
                      );
                    })}
                    {(!summary.categories || summary.categories.length === 0) && (
                      <p className="text-slate-500 text-center py-6">No behaviour data yet. Click "Analyse Reports" to start.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : selectedClient ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <Brain className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No behaviour data for this client yet</p>
                <p className="text-slate-500 text-sm mt-1">Click "Analyse Reports" to run AI behaviour extraction</p>
              </CardContent>
            </Card>
          ) : null}


          {/* Month-over-Month Comparison */}
          {comparison && comparison.comparison?.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700 mt-6">
              <CardHeader>
                <CardTitle className="text-white text-base">Month-over-Month Comparison</CardTitle>
                <p className="text-sm text-slate-400">{comparison.previous_month} vs {comparison.current_month}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-slate-900 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400">Previous Month</p>
                    <p className="text-xl font-bold text-white">{comparison.previous_total}</p>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400">Current Month</p>
                    <p className="text-xl font-bold text-white">{comparison.current_total}</p>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400">Change</p>
                    <p className={`text-xl font-bold ${comparison.total_change > 0 ? 'text-red-400' : comparison.total_change < 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                      {comparison.total_change > 0 ? '+' : ''}{comparison.total_change}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {comparison.comparison.map(c => {
                    const TrendIcon = TREND_ICONS[c.trend] || Minus;
                    const trendColor = c.trend === 'improving' ? 'text-emerald-400' : c.trend === 'worsening' ? 'text-red-400' : 'text-slate-400';
                    return (
                      <div key={c.category} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg" data-testid={`comparison-${c.category}`}>
                        <div className="flex items-center gap-3">
                          <TrendIcon className={`h-4 w-4 ${trendColor}`} />
                          <div>
                            <p className="text-sm text-white font-medium">{c.name}</p>
                            <p className="text-xs text-slate-500">
                              {c.previous_month.count} → {c.current_month.count} occurrences
                              {c.severity_change !== 0 && <span> | Severity: {c.severity_change > 0 ? '+' : ''}{c.severity_change}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${trendColor} border-current`}>
                            {c.count_change > 0 ? '+' : ''}{c.count_change}
                          </Badge>
                          <Badge variant="outline" className={`text-xs capitalize ${trendColor} border-current`}>{c.trend}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custom Behaviours */}
          <Card className="bg-slate-800/50 border-slate-700 mt-6">
            <CardHeader><CardTitle className="text-white text-base">Custom Behaviour Categories</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={newBehaviourName} onChange={(e) => setNewBehaviourName(e.target.value)} placeholder="New behaviour name..." className="bg-slate-900 border-slate-700 text-white flex-1" data-testid="custom-behaviour-name" />
                <Select value={newBehaviourSeverity} onValueChange={setNewBehaviourSeverity}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="positive">Positive</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddCustom} className="bg-teal-600 hover:bg-teal-700" data-testid="add-custom-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {categories.custom?.length > 0 && (
                <div className="space-y-2">
                  {categories.custom.map(c => (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={SEVERITY_COLORS[c.severity]}>{c.severity}</Badge>
                        <span className="text-sm text-white">{c.name}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-7" onClick={() => handleDeleteCustom(c.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
